import React, { useState } from 'react';
import { Button, Label } from 'design-system/Form';
import { TextInput, Select, Option, CircularProgress } from 'react-scale/components/design-system';
import SanitizedMarkdown from 'design-system/SanitizedMarkdown';
import apiFetch from '@scale/frontend-lib/api-fetch';
import { invokeAsyncCorpJob } from '@scale/frontend-lib/invokeCorpJob';

const FixWorkerLanguage: React.FC = () => {
  const [id, setId] = useState<string>('');
  const [taskAttempts, setTaskAttempts] = useState<string[]>([]);
  const [data, setData] = useState<{ language: string; count: number; email: string }[]>([]);
  const [singleLanguage, setSingleLanguage] = useState(false);
  const [languages, setLanguages] = useState<string[]>([]);
  const [selectedLanguage, setSelectedLanguage] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [jobResults, setJobResults] = useState<string>('');
  const [fixing, setFixing] = useState(false);
  const description = `
### Description:
- This tool allows you to fix the language selection for training attempts of a given tasker

##### Author: Rodrigo Soriano
---
`;

  const handleFind = async () => {
    setLoading(true);
    try {
      const response = await apiFetch(
        null,
        'corp-api/bulbasaur-utils/languages-from-worker-project',
        {
          method: 'PUT',
          body: JSON.stringify({
            workerId: id,
            projectId: '644b3744d39224195bc3e2e5',
          }),
          headers: {
            'Content-Type': 'application/json',
          },
        },
      );

      if (response.message) {
        setError(response.message);
        setLoading(false);
        return;
      }

      const attemptsByLanguage = response.attempts.reduce((acc: any, item: any) => {
        const id = Object.keys(item)[0];
        const language = item[id];

        if (acc[language]) {
          acc[language]++;
        } else {
          acc[language] = 1;
        }

        return acc;
      }, {});

      const dataFromResponse = Object.entries(attemptsByLanguage).map(([language, count]) => ({
        language,
        count: count as number,
        email: response.email as string,
      }));

      const attempts_ids = response.attempts.map((a: any) => Object.keys(a)[0]);

      if (dataFromResponse.length === 1) {
        setSingleLanguage(true);
      } else {
        setSingleLanguage(false);
      }
      setData(dataFromResponse);
      setLanguages(response.languages);
      setTaskAttempts(attempts_ids);
    } catch (error) {
      throw new Error(error.message);
    }
    setLoading(false);
  };

  const handleIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setId(value);

    if (!/^[0-9a-z]{24}$/.test(value)) {
      setError('Please enter a single valid Tasker ID');
    } else {
      setError(null);
    }
  };

  const handleFix = async () => {
    setJobResults('');
    setFixing(true);

    if (
      !window.confirm('Please confirm you wish to update these attempts for the identified Tasker.')
    ) {
      setLoading(false);
      return;
    }

    const jobParams = {
      attempt_ids: taskAttempts,
      lang: selectedLanguage,
    };
    let resultMessage = '## Job Results:\n\n';
    try {
      const job = await invokeAsyncCorpJob('fixWorkerLanguagesBulbaUtils', jobParams);
      resultMessage += `✅ Success: Updated attempts languages to:\n> ${JSON.stringify(
        selectedLanguage,
      )}\n\n[Job Status](https://dashboard.scale.com/corp/lookup/${job.id})\n\n`;
      resultMessage += `📝 Please note that the results will not automatically reflect on the permissions. Wait a couple of hours before contacting support.\n\n`;
    } catch (err) {
      resultMessage += ` ❌ Error ${err}|\n`;
    }
    setJobResults(resultMessage);
    setFixing(false);
  };

  const renderOutput = (text: string) => {
    return (
      <div className="response-markdown">
        <SanitizedMarkdown text={text || ''} />
        <style jsx global>{`
          .response-markdown tr {
            border: 1px solid rgba(0, 0, 0, 0.3);
          }

          .response-markdown tr:nth-child(even) {
            background-color: #f2f2f2;
          }

          .response-markdown th {
            border: 1px solid rgb(0, 0, 0, 0.3);
            padding: 6px 10px;
            background-color: #f2f2f2;
          }

          .response-markdown th {
            border: 1px solid rgba(255, 255, 255, 0.3);
            padding: 6px 10px;
            background-color: #222;
            color: white;
          }

          .response-markdown td {
            border: 1px solid rgba(0, 0, 0, 0.3);
            padding: 10px;
          }
        `}</style>
      </div>
    );
  };

  return (
    <div className="root">
      <section>
        <SanitizedMarkdown text={description} />
      </section>
      <section>
        <Label>Tasker ID:</Label>
        <TextInput
          style={{ width: '250px' }}
          value={id}
          onChange={handleIdChange}
          placeholder="Enter tasker id"
        />
        <Button primary onClick={handleFind} disabled={!!error || loading}>
          Find
        </Button>
        {error && <div style={{ color: 'red' }}>{error}</div>}
      </section>
      {loading ? (
        <div style={{ marginLeft: '50%', marginTop: 24, marginBottom: 52 }}>
          <CircularProgress size={50} />
        </div>
      ) : (
        data.length > 0 && (
          <section>
            <table
              style={{
                width: '100%',
                borderCollapse: 'collapse',
                textAlign: 'left',
                margin: '20px 0',
                border: '1px solid #ccc',
              }}
            >
              <thead>
                <tr style={{ backgroundColor: '#f3f3f3' }}>
                  <th style={{ padding: '10px', border: '1px solid #ccc' }}>Email</th>
                  <th style={{ padding: '10px', border: '1px solid #ccc' }}>Language</th>
                  <th style={{ padding: '10px', border: '1px solid #ccc' }}>Number of Attempts</th>
                </tr>
              </thead>
              <tbody>
                {data.map((attempt, index) => (
                  <tr key={index} style={{ borderBottom: '1px solid #ccc' }}>
                    <td style={{ padding: '10px', border: '1px solid #ccc' }}>{attempt.email}</td>
                    <td style={{ padding: '10px', border: '1px solid #ccc' }}>
                      {attempt.language}
                    </td>
                    <td style={{ padding: '10px', border: '1px solid #ccc' }}>{attempt.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {singleLanguage && (
              <Label
                style={{ color: 'orange', padding: '10px', marginTop: '10px', fontWeight: 'bold' }}
              >
                ⚠️ Seems like there are attempts only in one language. If you want to recompute
                them, please select the same language and click fix.
              </Label>
            )}
            <div
              style={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
              }}
            >
              <Label>Select the desired language for the tasker:</Label>
              <Select
                style={{ width: '100%', minWidth: 300 }}
                value={selectedLanguage}
                label={'Select the desired language for the tasker:'}
                placeholder={'Select a Language'}
                onChange={e => setSelectedLanguage(e.target.value as string)}
              >
                {languages.map(language => (
                  <Option key={language} value={language}>
                    {language}
                  </Option>
                ))}
              </Select>

              <Button
                disabled={!selectedLanguage}
                onClick={handleFix}
                style={{ marginTop: '20px' }}
                type="primary"
              >
                Fix
              </Button>
              {fixing && (
                <div style={{ marginLeft: '0%', marginTop: 24, marginBottom: 52 }}>
                  <CircularProgress size={50} />
                </div>
              )}
              {jobResults && renderOutput(jobResults)}
            </div>
          </section>
        )
      )}
    </div>
  );
};

export default FixWorkerLanguage;
